import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeSlash, MagnifyingGlass, XCircle, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: ReactNode;
  /** Server/validation message. Takes precedence over `helperText`. */
  error?: string;
  leadingIcon?: PhosphorIcon;
  trailingSlot?: ReactNode;
  containerClassName?: string;
  /** `filled` is the grey, 44px field used by the staff Settings forms (Figma A16). `compact` is the 42px white field with a 12px radius of the staff dialogs (Figma A04.1 "Add staff"). */
  variant?: 'outlined' | 'filled' | 'compact';
  /** Compact only: a grey cell fused to the right edge of the field (the fixed "@mybusinessmate.ai"). */
  addon?: ReactNode;
  /** Right-aligned on the label row (a link such as "Open", or a counter). */
  labelAside?: ReactNode;
  /** `compact` is the 14px text of the lead's and specialist's forms (Figma A05.x submit); the field's height is unchanged. */
  density?: 'default' | 'compact';
}

/**
 * Figma "Input Field" (21:127): 56px, radius 16, label above, helper/error below. The focus stroke is
 * an inset outline (no layout shift), 2px in the focus-ring colour, or error red when invalid.
 * React 19 passes `ref` as a normal prop, so React Hook Form's `{...register('x')}` works directly.
 */
export function Input({
  label,
  helperText,
  error,
  leadingIcon,
  trailingSlot,
  containerClassName,
  className,
  id,
  disabled,
  variant = 'outlined',
  addon,
  labelAside,
  density = 'default',
  ...props
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const messageId = `${inputId}-message`;
  const message = error ?? helperText;

  return (
    <div
      className={cx(
        'flex flex-col',
        variant === 'compact' ? 'gap-(--mbm-space-1-5)' : 'gap-(--mbm-space-2)',
        containerClassName,
      )}
    >
      {label ? (
        <div className="flex items-baseline justify-between gap-(--mbm-space-3)">
          <label
            htmlFor={inputId}
            className={cx('text-label-medium', variant === 'filled' ? 'text-text-primary' : 'text-text-secondary')}
          >
            {label}
          </label>
          {labelAside}
        </div>
      ) : null}

      <div
        className={cx(
          'flex items-center gap-(--mbm-space-2-5) overflow-hidden border',
          variant === 'compact' ? (addon ? 'pl-(--mbm-space-3-5)' : 'px-(--mbm-space-3-5)') : 'px-(--mbm-space-4)',
          variant === 'filled'
            ? 'h-(--mbm-size-input-filled) rounded-(--mbm-radius-sm) bg-surface-tint'
            : variant === 'compact'
              ? 'h-(--mbm-size-input-compact) rounded-(--mbm-radius-sm) bg-surface-card'
              : 'h-(--mbm-size-input) rounded-(--mbm-component-radius-input) bg-surface-card',
          'focus-within:outline-2 focus-within:-outline-offset-2',
          // Figma: an invalid field has a 1px red stroke; focusing it adds the 2px ring (red, not blue).
          error
            ? 'border-feedback-error focus-within:outline-feedback-error'
            : cx(
                variant === 'filled' ? 'border-transparent' : 'border-border-default',
                'focus-within:outline-focus-ring',
              ),
          disabled && 'bg-surface-tint opacity-(--mbm-opacity-disabled)',
        )}
      >
        {leadingIcon ? (
          <span className="text-text-tertiary">
            <Icon icon={leadingIcon} size="md" />
          </span>
        ) : null}
        <input
          id={inputId}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          className={cx(
            'h-full min-w-0 flex-1 bg-transparent text-text-primary outline-none placeholder:text-text-muted disabled:text-text-disabled',
            variant === 'compact' || density === 'compact' ? 'text-body-medium' : 'text-body-large',
            className,
          )}
          {...props}
        />
        {trailingSlot}
        {variant === 'compact' && addon ? (
          <span className="flex h-full items-center self-stretch bg-surface-tint px-(--mbm-space-3) text-body-small text-text-secondary">
            {addon}
          </span>
        ) : null}
      </div>

      {message ? (
        <p
          id={messageId}
          role={error ? 'alert' : undefined}
          className={cx(
            variant === 'compact' ? 'text-caption' : 'text-body-small',
            error ? 'text-feedback-error' : 'text-text-tertiary',
          )}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

/** Input with a show/hide toggle. Keeps the browser's password-manager hints via `autoComplete`. */
export function PasswordInput({ ...props }: Omit<InputProps, 'type' | 'trailingSlot'>) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      {...props}
      type={visible ? 'text' : 'password'}
      trailingSlot={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          className="text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring) rounded-full"
        >
          <Icon icon={visible ? EyeSlash : Eye} size="md" />
        </button>
      }
    />
  );
}

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  onClear?: () => void;
  /** `white` is the outlined 44px pill of the client's pages (Figma C03.1); the default is the grey 48px pill. */
  tone?: 'grey' | 'white';
  containerClassName?: string;
}

/**
 * Figma "Search Field" (116:995): a pill, not a variant of Input - grey at rest, white with a blue
 * stroke + soft ring when focused.
 */
export function SearchInput({
  value,
  onClear,
  tone = 'grey',
  containerClassName,
  className,
  'aria-label': ariaLabel,
  ...props
}: SearchInputProps) {
  const hasValue = typeof value === 'string' && value.length > 0;
  return (
    <div
      className={cx(
        'flex items-center gap-(--mbm-space-2-5) rounded-full px-(--mbm-space-4)',
        tone === 'white'
          ? 'h-(--mbm-size-input-filled) border border-border-default bg-surface-card'
          : 'h-(--mbm-size-search-field) bg-surface-field',
        'text-text-secondary transition-colors duration-(--mbm-duration-fast)',
        'focus-within:bg-surface-card focus-within:text-text-primary focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-brand-primary',
        containerClassName,
      )}
    >
      <Icon icon={MagnifyingGlass} size="md" />
      <input
        type="search"
        value={value}
        aria-label={ariaLabel ?? 'Search'}
        className={cx(
          'h-full min-w-0 flex-1 bg-transparent text-body-medium text-text-primary outline-none placeholder:text-text-secondary [&::-webkit-search-cancel-button]:hidden',
          className,
        )}
        {...props}
      />
      {hasValue ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={onClear}
          className="rounded-full text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={XCircle} size="sm" />
        </button>
      ) : null}
    </div>
  );
}
